import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { navLinks } from '@config';
import { loaderDelay } from '@utils';
import { useScrollDirection, usePrefersReducedMotion } from '@hooks';
import { Menu } from '@components';
import { IconLogo, IconHex } from '@components/icons';

const Nav = ({ isHome }) => {
  const [isMounted, setIsMounted] = useState(!isHome);
  // eslint-disable-next-line no-unused-vars
  const scrollDirection = useScrollDirection('down');
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleScroll = () => {
    setScrolledToTop(window.pageYOffset < 50);
  };

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    const timeout = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    return () => clearTimeout(timeout);
  }, [prefersReducedMotion]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const timeout = isHome ? loaderDelay : 0;
  const fadeClass = isHome ? 'fade' : '';
  const fadeDownClass = isHome ? 'fadedown' : '';

  // Dynamic scroll-based header styles (can't express media-query-conditional
  // transforms as Tailwind classes because they depend on runtime scroll state)
  const scrollStyles =
    !prefersReducedMotion && !scrolledToTop
      ? {
        height: 'var(--nav-scroll-height)',
        transform: `translateY(calc(var(--nav-scroll-height) * -1))`,
        boxShadow: '0 10px 30px -10px var(--navy-shadow)',
      }
      : {};

  const Logo = (
    // nav-logo hover class defined in global.css
    <div className="nav-logo flex justify-center items-center" tabIndex="-1">
      {isHome ? (
        <a href="/" aria-label="home" className="text-accent w-[42px] h-[42px] relative z-[1]">
          <div className="hex-container absolute top-0 left-0 z-[-1]">
            <IconHex />
          </div>
          <div className="logo-container relative z-[1]">
            <IconLogo />
          </div>
        </a>
      ) : (
        <Link
          to="/"
          aria-label="home"
          className="text-accent w-[42px] h-[42px] relative z-[1]">
          <div className="hex-container absolute top-0 left-0 z-[-1]">
            <IconHex />
          </div>
          <div className="logo-container relative z-[1]">
            <IconLogo />
          </div>
        </Link>
      )}
    </div>
  );

  const ResumeLink = (
    // resume-button class = smallButton styles (global.css)
    <a className="resume-button ml-[25px]" href="/resume.pdf" target="_blank" rel="noopener noreferrer">
      Resume
    </a>
  );

  return (
    <header
      className="flex justify-between items-center fixed top-0 z-[11] px-[50px] w-full h-[var(--nav-height)] bg-[var(--navy-shadow)] [filter:none!important] [pointer-events:auto!important] [user-select:auto!important] backdrop-blur-[10px] transition-all max-[1080px]:px-[40px] max-[768px]:px-[25px]"
      style={scrollStyles}>

      <nav
        className="flex justify-between items-center relative w-full text-lightest-slate font-mono z-[12]"
        style={{ counterReset: 'item 0' }}>

        {prefersReducedMotion ? (
          <>
            {Logo}

            {/* nav-links counter styles defined in global.css */}
            <div className="nav-links flex items-center max-[768px]:hidden">
              <ol>
                {navLinks &&
                  navLinks.map(({ url, name }, i) => (
                    <li key={i}>
                      <Link to={url}>{name}</Link>
                    </li>
                  ))}
              </ol>
              <div>{ResumeLink}</div>
            </div>

            <Menu />
          </>
        ) : (
          <>
            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout}>
                  <>{Logo}</>
                </CSSTransition>
              )}
            </TransitionGroup>

            <div className="nav-links flex items-center max-[768px]:hidden">
              <ol>
                <TransitionGroup component={null}>
                  {isMounted &&
                    navLinks &&
                    navLinks.map(({ url, name }, i) => (
                      <CSSTransition key={i} classNames={fadeDownClass} timeout={timeout}>
                        <li
                          key={i}
                          style={{ transitionDelay: `${isHome ? i * 100 : 0}ms` }}>
                          <Link to={url}>{name}</Link>
                        </li>
                      </CSSTransition>
                    ))}
                </TransitionGroup>
              </ol>

              <TransitionGroup component={null}>
                {isMounted && (
                  <CSSTransition classNames={fadeDownClass} timeout={timeout}>
                    <div
                      style={{
                        transitionDelay: `${isHome ? navLinks.length * 100 : 0}ms`,
                      }}>
                      {ResumeLink}
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>
            </div>

            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout}>
                  <Menu />
                </CSSTransition>
              )}
            </TransitionGroup>
          </>
        )}
      </nav>
    </header>
  );
};

Nav.propTypes = {
  isHome: PropTypes.bool,
};

export default Nav;
